import { useRef, useState } from 'react';
import {
  Add,
  ContentCopy,
  DeleteOutline,
  Download,
  ExpandMore,
  PlayArrow,
  RestartAlt,
  Stop,
  Upload,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { API_BUSINESS_URL } from '@/utils';

import { Flow, FlowStep } from './models';
import { useFlows } from './hooks/useFlows';
import { useRunner } from './hooks/useRunner';
import VariablesEditor from './components/VariablesEditor';
import StepEditor from './components/StepEditor';
import ResultsPanel from './components/ResultsPanel';
import LinksPanel from './components/LinksPanel';

const newStep = (): FlowStep => ({
  id: crypto.randomUUID(),
  name: 'Paso nuevo',
  method: 'GET',
  path: '/',
  headers: {},
  bodyMode: 'none',
  body: '',
  fields: [],
  captures: [],
  expectStatus: '',
  enabled: true,
});

const TechChecks = () => {
  const {
    flows,
    selected,
    selectedId,
    setSelectedId,
    updateFlow,
    createFlow,
    duplicateFlow,
    removeFlow,
    restoreSeeds,
    importFlows,
  } = useFlows();

  const { results, variables, running, run, stop, reset } = useRunner();
  const [notice, setNotice] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  if (!selected) return null;

  const setFlow = (patch: Partial<Flow>) =>
    updateFlow({ ...selected, ...patch });

  const setStep = (index: number, step: FlowStep) => {
    const steps = [...selected.steps];
    steps[index] = step;
    setFlow({ steps });
  };

  const moveStep = (index: number, offset: number) => {
    const target = index + offset;
    if (target < 0 || target >= selected.steps.length) return;
    const steps = [...selected.steps];
    [steps[index], steps[target]] = [steps[target], steps[index]];
    setFlow({ steps });
  };

  const exportFlow = () => {
    const blob = new Blob([JSON.stringify(selected, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${selected.name.replace(/\s+/g, '-').toLowerCase()}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const importFile = async (file: File) => {
    try {
      const imported = importFlows(await file.text());
      setNotice(`Se importaron ${imported} secuencia(s).`);
    } catch (error) {
      setNotice(
        error instanceof Error
          ? `No se pudo importar: ${error.message}`
          : 'No se pudo importar el archivo'
      );
    }
  };

  const enabledSteps = selected.steps.filter((step) => step.enabled).length;
  const failedSteps = results.filter((result) => result.state === 'failed').length;
  const passedSteps = results.filter((result) => result.state === 'ok').length;

  return (
    <Box p={3}>
      <Typography variant='h5' mb={0.5}>
        Pruebas técnicas
      </Typography>
      <Typography variant='body2' color='text.secondary' mb={2}>
        Corre secuencias de endpoints contra{' '}
        <strong>{API_BUSINESS_URL}</strong> con tu propia sesión. Lo que crea
        queda en el ambiente: en producción son datos reales.
      </Typography>

      {notice && (
        <Alert severity='info' onClose={() => setNotice('')} sx={{ mb: 2 }}>
          {notice}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0 }}>
          <Paper variant='outlined'>
            <List dense disablePadding>
              {flows.map((flow) => (
                <ListItemButton
                  key={flow.id}
                  selected={flow.id === selectedId}
                  onClick={() => {
                    setSelectedId(flow.id);
                    reset();
                  }}
                >
                  <ListItemText
                    primary={flow.name}
                    secondary={`${flow.steps.length} paso(s)`}
                  />
                </ListItemButton>
              ))}
            </List>
            <Divider />
            <Stack spacing={0.5} p={1}>
              <Button size='small' startIcon={<Add />} onClick={createFlow}>
                Nueva secuencia
              </Button>
              <Button
                size='small'
                startIcon={<ContentCopy />}
                onClick={() => duplicateFlow(selected)}
              >
                Duplicar
              </Button>
              <Button
                size='small'
                startIcon={<Download />}
                onClick={exportFlow}
              >
                Exportar JSON
              </Button>
              <Button
                size='small'
                startIcon={<Upload />}
                onClick={() => fileInput.current?.click()}
              >
                Importar JSON
              </Button>
              <Button
                size='small'
                startIcon={<RestartAlt />}
                onClick={restoreSeeds}
              >
                Restaurar las de fábrica
              </Button>
              <Button
                size='small'
                color='error'
                startIcon={<DeleteOutline />}
                onClick={() => removeFlow(selected.id)}
              >
                Eliminar
              </Button>
            </Stack>
            <input
              ref={fileInput}
              type='file'
              accept='application/json'
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) importFile(file);
                event.target.value = '';
              }}
            />
          </Paper>
        </Box>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack spacing={2}>
            <Paper variant='outlined' sx={{ p: 2 }}>
              <Stack spacing={2}>
                <TextField
                  size='small'
                  label='Nombre'
                  value={selected.name}
                  onChange={(event) => setFlow({ name: event.target.value })}
                  fullWidth
                />
                <TextField
                  size='small'
                  label='Qué prueba'
                  value={selected.description}
                  onChange={(event) =>
                    setFlow({ description: event.target.value })
                  }
                  multiline
                  minRows={2}
                  fullWidth
                />

                <Stack direction='row' spacing={1} alignItems='center'>
                  <Button
                    variant='contained'
                    startIcon={<PlayArrow />}
                    disabled={running || !enabledSteps}
                    onClick={() => run(selected)}
                  >
                    Ejecutar {enabledSteps} paso(s)
                  </Button>
                  <Button
                    startIcon={<Stop />}
                    disabled={!running}
                    onClick={stop}
                  >
                    Detener
                  </Button>
                  {results.length > 0 && (
                    <>
                      <Chip
                        size='small'
                        color='success'
                        variant='outlined'
                        label={`${passedSteps} ok`}
                      />
                      {failedSteps > 0 && (
                        <Chip
                          size='small'
                          color='error'
                          variant='outlined'
                          label={`${failedSteps} con error`}
                        />
                      )}
                    </>
                  )}
                </Stack>
              </Stack>
            </Paper>

            <Accordion variant='outlined' defaultExpanded={false}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography>Variables</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <VariablesEditor
                  variables={selected.variables}
                  onChange={(next) => setFlow({ variables: next })}
                />
              </AccordionDetails>
            </Accordion>

            <Paper variant='outlined' sx={{ p: 2 }}>
              <Typography variant='subtitle1' mb={1}>
                Pasos
              </Typography>
              <Stack spacing={1}>
                {selected.steps.map((step, index) => (
                  <StepEditor
                    key={step.id}
                    step={step}
                    index={index}
                    total={selected.steps.length}
                    onChange={(updated) => setStep(index, updated)}
                    onRemove={() =>
                      setFlow({
                        steps: selected.steps.filter((_, i) => i !== index),
                      })
                    }
                    onMove={(offset) => moveStep(index, offset)}
                  />
                ))}
              </Stack>
              <Button
                size='small'
                startIcon={<Add />}
                sx={{ mt: 1 }}
                onClick={() =>
                  setFlow({ steps: [...selected.steps, newStep()] })
                }
              >
                Agregar paso
              </Button>
            </Paper>

            <Paper variant='outlined' sx={{ p: 2 }}>
              <Typography variant='subtitle1' mb={1}>
                Links
              </Typography>
              <LinksPanel
                links={selected.links}
                variables={variables}
                onChange={(next) => setFlow({ links: next })}
              />
            </Paper>

            <Paper variant='outlined' sx={{ p: 2 }}>
              <Typography variant='subtitle1' mb={1}>
                Resultados
              </Typography>
              <ResultsPanel results={results} />
            </Paper>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

export default TechChecks;
